import { Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router';
import AppButton from 'src/components/appComponents/AppButton';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useApproveParentSuggestionMutation, useRejectParentSuggestionMutation } from 'src/graphql';
import { useBoolean } from 'src/hooks/use-boolean';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

type Props = {
  suggestion: any;
};

export default function ParentSuggestionsButtons({ suggestion }: Props) {
  const confirmApprove = useBoolean();
  const confirmReject = useBoolean();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { suggestedId } = useParams();

  const { mutate: mutateApprove, isLoading: isLoadingApprove } = useApproveParentSuggestionMutation(
    {
      onSuccess: () => {
        confirmApprove.onFalse();
        confirmReject.onFalse();
        enqueueSnackbar({ variant: 'success', message: 'Completed Successfully' });
        queryClient.refetchQueries(['GetParentSuggestionById']);
      },
    }
  );

  const { mutate: mutateReject, isLoading: isLoadingReject } = useRejectParentSuggestionMutation({
    onSuccess: () => {
      confirmApprove.onFalse();
      confirmReject.onFalse();
      enqueueSnackbar({ variant: 'success', message: 'Completed Successfully' });
      queryClient.refetchQueries(['GetParentSuggestionById']);
    },
  });

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" mt={2}>
        {suggestion?.status === 'Pending' && (
          <>
            <Grid item>
              <AppButton label="Reject" onClick={confirmReject.onTrue} />
            </Grid>
            <Grid item>
              <AppButton label="Approve" color="primary" onClick={confirmApprove.onTrue} />
            </Grid>
          </>
        )}
        {suggestion?.status === 'Approved' && (
          <Grid item>
            <AppButton
              label="Publish"
              color="primary"
              linkCompoennt={RouterLink}
              href={paths.dashboard.parentSuggestions.publishSuggestion(suggestedId ?? '')}
            />
          </Grid>
        )}
      </Grid>

      {/* Approve Dialog */}
      <ConfirmDialog
        open={confirmApprove.value}
        onClose={confirmApprove.onFalse}
        content="Do you really want to approve this suggestion?"
        icon="eva:edit-2-fill"
        action={
          <AppButton
            label="Yes"
            color="primary"
            isLoading={isLoadingApprove}
            onClick={() => mutateApprove({ suggestedId })}
          />
        }
      />

      {/* Reject Dialog */}
      <ConfirmDialog
        open={confirmReject.value}
        onClose={confirmReject.onFalse}
        content="Do you really want to reject this suggestion?"
        icon="eva:edit-2-fill"
        action={
          <AppButton
            label="Yes"
            color="primary"
            isLoading={isLoadingReject}
            onClick={() => mutateReject({ suggestedId })}
          />
        }
      />
    </>
  );
}
